import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Panel } from 'primereact/panel';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';

import { ShipmentStatusTag } from '@/components';
import { ShipmentNumberLink } from '@/components/common/shipments-grid/ShipmentNumberLink';
import { shipmentStatusOptions } from '@/constants';
import { getMissingShipmentFields } from '@/pages/semen-app/util/util';
import { ShipmentDto } from '@/services/semen-service';

type ShipmentDetailsPanelProps = {
  selectedShipment: ShipmentDto | null;
  onRemoveSemenFromShipment: (semenId: string) => void;
  isShipmentLocked?: boolean;
};

export const ShipmentDetailsPanel = ({
  selectedShipment,
  onRemoveSemenFromShipment,
  isShipmentLocked = false,
}: ShipmentDetailsPanelProps) => {
  const { t } = useTranslation();

  const headerTemplate = () => {
    const shipmentStatus = shipmentStatusOptions.find(
      (status: any) => status.value === selectedShipment?.shipmentStatus,
    )?.label;
    const isDraft = shipmentStatus === 'Scheduled';
    const missingFields = getMissingShipmentFields(selectedShipment);
    const isIncomplete = missingFields.length > 0;

    return (
      <div className="p-panel-header" data-pc-section="header">
        <div className="flex w-full items-center justify-between">
          <span className="p-panel-title" data-pc-section="title">
            {t('shipping.shipmentDetails')}
          </span>
          {isDraft ? (
            <span className="inline-flex items-center rounded-md bg-orange-500 px-2 py-1 text-xs font-medium text-white">
              Draft
            </span>
          ) : isIncomplete ? (
            <span className="incomplete-badge inline-flex cursor-help items-center rounded-md bg-yellow-500 px-2 py-1 text-xs font-medium text-white">
              Incomplete
            </span>
          ) : null}
        </div>
      </div>
    );
  };

  if (!selectedShipment) {
    return (
      <Panel
        header={t('shipping.shipmentDetails')}
        className="flex h-full flex-col"
      >
        <div className="text-sm text-gray-600">
          {t('shipping.selectShipment')}
        </div>
      </Panel>
    );
  }

  const missingFields = getMissingShipmentFields(selectedShipment);
  const isIncomplete = missingFields.length > 0;

  return (
    <>
      {isIncomplete && (
        <Tooltip
          target=".incomplete-badge"
          position="top"
          content={`Missing fields: ${missingFields.join(', ')}`}
          className="max-w-md"
        />
      )}
      <Panel
        headerTemplate={headerTemplate()}
        className="flex h-full flex-col overflow-hidden"
      >
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4">
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t('shipping.shipmentNumber')}
                </label>
                <div className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm">
                  {selectedShipment?.shipmentNumber ? (
                    <ShipmentNumberLink
                      shipmentId={selectedShipment.id}
                      shipmentNumber={selectedShipment.shipmentNumber}
                    />
                  ) : (
                    'N/A'
                  )}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t('shipping.shipTo')}
                </label>
                <div className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm">
                  {selectedShipment?.shipToName || 'N/A'}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t('shipping.shipmentStatus')}
                </label>
                {selectedShipment?.shipmentStatus ? (
                  <ShipmentStatusTag value={selectedShipment.shipmentStatus} />
                ) : (
                  'N/A'
                )}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t('common.comments')}
              </label>
              <div className="min-h-[50px] w-full rounded-md border border-gray-300 bg-gray-50 p-2 text-sm">
                {selectedShipment?.comment || 'No comments'}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t('shipping.trackingNumber')}
              </label>
              <div className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm">
                {selectedShipment?.trackingNumber || 'N/A'}
              </div>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                {t('semen.semen')}
              </label>
              <div className="rounded-md border border-gray-200">
                <DataTable
                  value={selectedShipment?.semens || []}
                  emptyMessage="No Straws Selected"
                  size="small"
                  className="p-datatable-sm"
                  scrollable
                  scrollHeight="25rem"
                >
                  <Column field="name" header={t('common.name')} />
                  <Column field="sireName" header={t('sire.sireName')} />
                  <Column field="cane" header={t('semen.cane')} />
                  <Column field="quantity" header={t('fields.quantity')} />
                  <Column
                    field="quantityToShip"
                    header={t('shipping.scheduledPulledToShip')}
                  />
                  <Column
                    header={t('common.actions')}
                    body={(rowData: any) => (
                      <Button
                        icon="pi pi-trash"
                        size="small"
                        severity="danger"
                        text
                        tooltip={
                          isShipmentLocked
                            ? 'Cannot remove from locked shipment'
                            : 'Remove from shipment'
                        }
                        disabled={isShipmentLocked}
                        onClick={() => onRemoveSemenFromShipment(rowData.id)}
                      />
                    )}
                    style={{ width: '80px' }}
                  />
                </DataTable>
              </div>
            </div>
          </div>
        </div>
      </Panel>
    </>
  );
};
